import { useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import BaseTableCard from 'common/components/BaseTableCard'
import BasicTable from 'common/components/BasicTable'
import { DateRange } from 'common/components/DateRangePicker'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { FeatureKey } from 'common/constants/permission'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { getShrimpMortalityKey } from 'common/constants/queryKeys'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { useMutateDeleteDeadShrimp } from 'modules/deadShrimp/api/mutation'
import { useDeadShrimps } from 'modules/deadShrimp/api/selector'
import {
  deadShrimpDateTableFilterState,
  selectedDeadShrimpIdState,
} from 'modules/deadShrimp/stores/atom'
import { dashboardDateDisplayValue } from 'modules/food/utils'
import { useSpeciesName } from 'modules/pond/api/selector'
import MoreAction from 'modules/pond/components/MoreAction'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useRecoilState } from 'recoil'
import { normalizeSpeciesName } from 'utils/analytics'

import CircularImageGallery from 'components/CircularImageGallery'

import DeadShrimpDialog from '../DeadShrimpDialog'

const ShrimpDeadTable = () => {
  const { t } = useTranslation('deadshrimp')
  const queryClient = useQueryClient()
  const { data, isFetching, fetchNextPage, hasNextPage } = useDeadShrimps('table')

  const [open, setOpen] = useState(false)
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  })

  const [deadShrimpId, setDeadShrimpId] = useRecoilState(selectedDeadShrimpIdState)

  const [deadShrimpDateTableFilter, setDeadShrimpDateTableFilter] = useRecoilState(
    deadShrimpDateTableFilterState
  )

  const deleteDeadShrimpMutation = useMutateDeleteDeadShrimp()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const handleDateRangeChange = async (range: DateRange) => {
    setDateRange(range)
    setDeadShrimpDateTableFilter({
      setDate: range.startDate ?? undefined,
      endDate: range.endDate ?? undefined,
    })
    await queryClient.invalidateQueries({ queryKey: getShrimpMortalityKey.all() })
  }

  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      await deleteDeadShrimpMutation.mutateAsync(Number(deadShrimpId), {
        onSuccess: (res: AxiosResponse<any>) => {
          const { status } = res.data
          if (status !== 200) return
          const normalizedSpecies = normalizeSpeciesName(speciesName)
          trackEvent(
            AnalyticsEvents.mortalityRecordDeleted,
            normalizedSpecies ? { species: normalizedSpecies } : undefined
          )
        },
      })
    },
  })

  const onLoadMoreRows = () => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  const columns = [
    {
      id: 'datetime',
      title: t('date.time'),
      format: (val?: string) =>
        val ? formatThaiShortDateTwoDigitYear(val, { showTime: true }) : '-',
      width: '200px',
    },
    {
      id: 'shrimpAge',
      title: <Trans i18nKey={'pond:animal.age'} values={{ species: speciesName }} />,
      width: '100px',
    },
    {
      id: 'amountNew',
      title: t('amount.new.unit'),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'amountOld',
      title: t('amount.old.unit'),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'total',
      title: t('total.unit'),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'accumulate',
      title: t('acc.unit'),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'reason',
      title: t('death.reason'),
      width: '150px',
    },
    {
      id: 'image',
      title: t('image'),
      width: '64px',
    },
    {
      id: 'deadShrimpId',
      title: '',
      width: '100px',
    },
  ]

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpenEdit = (id: number) => {
    setDeadShrimpId(id)
    setOpen(true)
  }

  const handleOpenDeleteConfirm = (id: number) => {
    setDeadShrimpId(id)
    openConfirmDialog()
  }

  const createTableRows = (
    datetime: string,
    shrimpAge: number,
    amountNew: number,
    amountOld: number,
    total: number,
    accumulate: number,
    reason: string,
    imgUrls: string[] | null,
    deadShrimpId: number
  ) => ({
    datetime,
    shrimpAge,
    amountNew,
    amountOld,
    total,
    accumulate,
    reason,
    image:
      imgUrls && imgUrls.length > 0 ? (
        <CircularImageGallery images={imgUrls} alt={`${datetime}-${reason}`} size="64px" />
      ) : (
        '-'
      ),
    deadShrimpId: (
      <MoreAction
        featureKey={FeatureKey.Death}
        isHideOnCompleted={false}
        onClickEdit={() => handleOpenEdit(deadShrimpId)}
        onClickDelete={() => handleOpenDeleteConfirm(deadShrimpId)}
      />
    ),
  })
  const rows = data.map((el) => {
    const allImages: string[] = []
    if (el.img_urls && el.img_urls.length > 0) {
      allImages.push(...el.img_urls)
    }
    if (el.img_url && !allImages.includes(el.img_url)) {
      allImages.push(el.img_url)
    }
    return createTableRows(
      el.datetime,
      el.shrimpAge,
      el.newDeadShrimpCountInKg,
      el.oldDeadShrimpCountInKg,
      el.totalDeadShrimp,
      el.accumulatedDeadShrimp,
      el.causeOfDeath,
      allImages.length > 0 ? allImages : null,
      el.id
    )
  })

  return (
    <>
      {dialog}
      <BaseTableCard
        title={t('summary.dead.species', { speciesName })}
        refresh={async () =>
          await queryClient.invalidateQueries({ queryKey: getShrimpMortalityKey.all() })
        }
        isLoading={isFetching}
        isEmpty={data.length === 0}
        isDateRangePicker
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        displayValue={dashboardDateDisplayValue}
      >
        <BasicTable
          columns={columns}
          onLoading={onLoadMoreRows}
          isLoading={isFetching}
          rows={rows}
        />
      </BaseTableCard>
      {open && <DeadShrimpDialog onClose={handleClose} open={open} isEdit />}
    </>
  )
}

export default ShrimpDeadTable
